// UX-Vorschlag P1 („Mein Tag" als Startseite): reine Logik fuer die rechte
// Spalte — „Braucht dich" (ueberfaellig, blockiert, Erwaehnungen, faellige
// Nachfragen, je mit GENAU einer Aktion) und die Vorschau „Diese Woche".
// Keine Signale, kein Fetch: alles hier ist mit festen Eingaben testbar.

import type { InboxItemDto, NeedsYouDto, OpenTaskDto, PersonalTask, Task } from "@juuplan/shared";

/** Projektaufgabe aus GET /api/me/tasks (inkl. additiver UX-Felder). */
export interface MeTaskLite extends Task {
  project_id: number;
  project_name: string;
}

export type NeedsYouKind = "overdue" | "blocked" | "mention" | "follow_up";
/** Genau eine Aktion pro Zeile. */
export type NeedsYouAction = "today" | "reply" | "open";

export interface NeedsYouRow {
  key: string;
  kind: NeedsYouKind;
  title: string;
  /** Zweite Zeile, z. B. „überfällig seit 24.10. · M365-Migration". */
  context: string;
  /** Optionaler Zitat-Auszug (Erwaehnung). */
  quote: string | null;
  action: NeedsYouAction;
  /** Ziel fuer → Heute / Oeffnen / Antworten. */
  ref: {
    kind: "task" | "personal" | "follow_up" | "inbox";
    id: number;
    project_id: number | null;
    /** Fuer Antworten: Projektaufgabe, an die der Kommentar geht. */
    task_id: number | null;
    link: string | null;
    source_type?: string;
  };
}

export interface NeedsYouInput {
  /** Tag der Ansicht (YYYY-MM-DD, lokal). */
  day: string;
  /** GET /api/me/needs-you — der Server buendelt die vier Quellen. */
  needsYou: NeedsYouDto;
  /** Schluessel „task-<id>"/„personal-<id>" von allem, was schon in „Heute" steht. */
  todayKeys: Set<string>;
}

/** Kurzdatum „24.10." ohne Jahr — die Spalte ist schmal. */
export function shortDate(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split("-");
  return d && m ? `${d}.${m}.` : iso;
}

function isTaskOpen(t: Task): boolean {
  const cat = t.statusInfo?.category;
  if (cat) return cat !== "done" && cat !== "cancelled";
  return t.status !== "done" && t.completed_at === null;
}

function taskDue(t: Task): string | null {
  return (t.deadline ?? t.end_date ?? null)?.slice(0, 10) ?? null;
}

/** Top-Level, offen — persoenliche „Schritte" SIND verschachtelte personal_tasks. */
function isOpenTopLevelPersonal(p: PersonalTask): boolean {
  return !p.is_done && (p.parent_id === null || p.parent_id === undefined);
}

function openTaskRef(t: OpenTaskDto): NeedsYouRow["ref"] {
  const kind = t.kind === "project" ? "task" : "personal";
  const projectId = t.source.type === "project" ? t.source.id : null;
  return { kind, id: t.id, project_id: projectId, task_id: kind === "task" ? t.id : null, link: null };
}

function todayKey(t: OpenTaskDto): string {
  return `${t.kind === "project" ? "task" : "personal"}-${t.id}`;
}

/**
 * Zeilen fuer „Braucht dich" in fester Reihenfolge: ueberfaellig → blockiert →
 * Erwaehnungen → faellige Nachfragen. Was schon in „Heute" steht, faellt raus;
 * eine ueberfaellige Aufgabe erscheint nicht zusaetzlich als blockiert.
 */
export function buildNeedsYou(input: NeedsYouInput): NeedsYouRow[] {
  const { needsYou: n, todayKeys } = input;
  const rows: NeedsYouRow[] = [];
  const seen = new Set<string>();

  const byDue = (a: OpenTaskDto, b: OpenTaskDto): number =>
    (a.due_date ?? "9999").localeCompare(b.due_date ?? "9999");

  for (const t of [...n.overdue].sort(byDue)) {
    const key = todayKey(t);
    if (todayKeys.has(key) || seen.has(key)) continue;
    seen.add(key);
    rows.push({
      key: `overdue-${key}`,
      kind: "overdue",
      title: t.title,
      context: `überfällig seit ${shortDate(t.due_date ?? "")} · ${t.kind === "project" ? t.source.name : "Persönlich"}`,
      quote: null,
      action: "today",
      ref: openTaskRef(t),
    });
  }

  for (const t of [...n.blocked].sort(byDue)) {
    const key = todayKey(t);
    if (todayKeys.has(key) || seen.has(key)) continue;
    seen.add(key);
    const first = t.blocked_by[0];
    const by = first
      ? `wartet auf „${first.title}"${t.blocked_by.length > 1 ? ` (+${t.blocked_by.length - 1})` : ""}`
      : "blockiert";
    rows.push({
      key: `blocked-${key}`,
      kind: "blocked",
      title: t.title,
      context: `${by} · ${t.source.name}`,
      quote: null,
      action: "today",
      ref: openTaskRef(t),
    });
  }

  const newestFirst = (a: InboxItemDto, b: InboxItemDto): number =>
    b.created_at.localeCompare(a.created_at);

  for (const i of [...n.mentions].sort(newestFirst)) {
    if (i.self_ping) continue;
    const where = i.task_title ? `in „${i.task_title}"` : "";
    rows.push({
      key: `mention-${i.source_type}-${i.source_id}`,
      kind: "mention",
      title: i.actor_name ? `${i.actor_name} ${where}`.trim() : i.title,
      context: i.project_name ?? "Erwähnung",
      quote: i.body,
      action: i.can_reply ? "reply" : "open",
      ref: {
        kind: "inbox",
        id: i.source_id,
        project_id: i.project_id,
        task_id: i.task_id,
        link: i.link,
        source_type: i.source_type,
      },
    });
  }

  for (const i of [...n.follow_ups].sort(newestFirst)) {
    rows.push({
      key: `follow-up-${i.source_type}-${i.source_id}`,
      kind: "follow_up",
      title: i.title,
      context: i.task_title ? `„${i.task_title}"` : i.kind === "reminder" ? "Erinnerung" : "Nachfrage",
      quote: i.body,
      action: "open",
      ref: {
        kind: "inbox",
        id: i.source_id,
        project_id: i.project_id,
        task_id: i.task_id,
        link: i.link ?? "/follow-ups",
        source_type: i.source_type,
      },
    });
  }

  return rows;
}

function toLocalIsoDay(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

/** Alle ueberfaelligen Zeilen, die sich per „→ Heute" uebernehmen lassen. */
export function overdueRefs(rows: NeedsYouRow[]): NeedsYouRow["ref"][] {
  return rows.filter((r) => r.kind === "overdue").map((r) => r.ref);
}

// ---------- „Diese Woche" ----------

export interface WeekRow {
  key: string;
  title: string;
  due: string;
  /** „Mi 28.10." */
  dueLabel: string;
  kind: "task" | "personal";
  id: number;
  project_id: number | null;
}

const WEEKDAY = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

/** Letzter Tag (Sonntag) der Kalenderwoche von `day`. */
export function endOfWeek(day: string): string {
  const d = new Date(`${day}T00:00:00`);
  const dow = d.getDay(); // 0 = So
  d.setDate(d.getDate() + (dow === 0 ? 0 : 7 - dow));
  return toLocalIsoDay(d.toISOString()) ?? day;
}

/**
 * Offene Aufgaben, die NACH `day` und bis Sonntag dieser Woche faellig sind
 * und noch nicht in „Heute" stehen. Sortiert nach Faelligkeit.
 */
export function buildWeekPreview(input: {
  day: string;
  projectTasks: MeTaskLite[];
  personalTasks: PersonalTask[];
  todayKeys: Set<string>;
}): WeekRow[] {
  const end = endOfWeek(input.day);
  const out: WeekRow[] = [];
  const add = (r: Omit<WeekRow, "dueLabel">): void => {
    const d = new Date(`${r.due}T00:00:00`);
    out.push({ ...r, dueLabel: `${WEEKDAY[d.getDay()]} ${shortDate(r.due)}` });
  };
  for (const t of input.projectTasks) {
    if (!isTaskOpen(t) || input.todayKeys.has(`task-${t.id}`)) continue;
    const due = taskDue(t);
    if (due && due > input.day && due <= end) {
      add({ key: `task-${t.id}`, title: t.title, due, kind: "task", id: t.id, project_id: t.project_id });
    }
  }
  for (const p of input.personalTasks) {
    if (!isOpenTopLevelPersonal(p) || input.todayKeys.has(`personal-${p.id}`)) continue;
    const due = p.due_date?.slice(0, 10) ?? null;
    if (due && due > input.day && due <= end) {
      add({ key: `personal-${p.id}`, title: p.title, due, kind: "personal", id: p.id, project_id: null });
    }
  }
  out.sort((a, b) => (a.due < b.due ? -1 : a.due > b.due ? 1 : a.title.localeCompare(b.title)));
  return out;
}
